import React from 'react';
import { Components } from 'react-markdown';
import { MessageModel } from '../../types/models';
import { UserMessageMenu } from '../UserMessageMenu';
import { MarkdownRenderer } from './MarkdownRenderer';
import { MessageImages } from './MessageImages';

interface UserMessageProps {
	message: MessageModel;
	components: Components;
	messageContentRef: React.RefObject<HTMLDivElement | null>;
	handleCopy: (content: string) => void;
	handleCopyEvent: (e: React.ClipboardEvent<HTMLDivElement>) => void;
	// Edit hook state
	isEditing: boolean;
	editedContent: string;
	setEditedContent: React.Dispatch<React.SetStateAction<string>>;
	editTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
	handleEdit: () => void;
	handleSaveEdit: () => void;
	handleCancelEdit: () => void;
	handleEditKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	// Audio hook state
	isRecording: boolean;
	isTranscribing: boolean;
	handleAudioClick: () => void;
}

export const UserMessage = React.memo(function UserMessage({
	message,
	components,
	messageContentRef,
	handleCopy,
	handleCopyEvent,
	isEditing,
	editedContent,
	setEditedContent,
	editTextareaRef,
	handleEdit,
	handleSaveEdit,
	handleCancelEdit,
	handleEditKeyDown,
	isRecording,
	isTranscribing,
	handleAudioClick,
}: UserMessageProps) {
	return (
		<>
			<div className="flex flex-col items-end mr-6 max-w-[40%] self-end">
				<>
					{isEditing ? (
						<div
							className="bg-neutral-500 px-lg py-sm rounded-[10px_10px_0px_10px] user-message-editing-frame"
							style={{
								width: '500px',
								maxWidth: '100%',
								boxSizing: 'border-box',
							}}
						>
							<textarea
								ref={editTextareaRef}
								value={editedContent}
								onChange={(e) => setEditedContent(e.target.value)}
								onKeyDown={handleEditKeyDown}
								placeholder="Edit your message here..."
								className="w-full bg-transparent text-neutral-100 text-md resize-none border-0 outline-none overflow-y-auto placeholder:text-neutral-100 text-md italic"
								style={{
									minHeight: '50px',
									maxHeight: '500px',
									boxSizing: 'border-box',
								}}
							/>
							<div className="flex gap-2 mt-2 justify-end items-center">
								<button
									onClick={handleCancelEdit}
									className="px-3 py-1.5 text-sm text-neutral-100 rounded hover:bg-red-500 transition-colors flex items-center justify-center min-h-[32px]"
									title="Cancel"
									aria-label="Cancel"
								>
									<i className="fas fa-times text-lg"></i>
								</button>

								<button
									onClick={handleAudioClick}
									disabled={isTranscribing}
									aria-label={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Record voice'}
									className={`
										px-3 py-1.5 text-sm text-neutral-100 rounded
										transition-all duration-200 ease-in-out
										flex items-center justify-center min-h-[32px]
										${isTranscribing ? 'bg-neutral-500/20' : 'hover:bg-neutral-400'}
									`}
									title={
										isRecording
											? 'Stop recording'
											: isTranscribing
												? 'Transcribing...'
												: 'Record voice'
									}
								>
									{isTranscribing ? (
										<i className="fas fa-spinner fa-spin text-lg"></i>
									) : (
										<i
											className={`
												fas fa-microphone text-lg
												${isRecording ? 'mic-pulse recording-ring' : ''}
												transition-colors duration-200
											`}
											style={{ color: isRecording ? '#ef4444' : '#f5f5f5' }}
										/>
									)}
								</button>

								<button
									onClick={handleSaveEdit}
									className="px-3 py-1.5 text-sm text-neutral-100 rounded hover:bg-green-600 transition-colors flex items-center justify-center min-h-[32px]"
									title="Send"
									aria-label="Send"
								>
									<i className="fas fa-paper-plane"></i>
								</button>
							</div>
						</div>
					) : (
						<div
							ref={messageContentRef}
							className="bg-red-600 px-lg py-sm rounded-[10px_10px_0px_10px] whitespace-pre-wrap w-full relative markdown-container"
							style={{
								wordWrap: 'break-word',
								overflowWrap: 'break-word',
								minWidth: 0,
							}}
							onCopy={handleCopyEvent}
						>
							<MarkdownRenderer
								content={message.content.trim()}
								components={components}
							/>
						</div>
					)}
					{!isEditing && (
						<div className="flex justify-end items-center mt-1">
							<UserMessageMenu
								onCopy={() => handleCopy(message.content)}
								onEdit={handleEdit}
							/>
						</div>
					)}
				</>
			</div>

			{message?.images && message.images.length > 0 && (
				<MessageImages images={message.images} />
			)}
		</>
	);
});
